// ADR 7.10.26v §2 — the spread, composed from the facade's answer. Pure.
//
// The rule is rust-core's (7.10.26u); this module only maps its answer onto the
// web's two pages. u's `split_dp` is **physical** (the left page's width), so
// this is where physical becomes leading: in LTR the leading page is the left
// one, `split` wide; in RTL it is the right one, `w − split − gap` wide, and the
// companion is the left page. Each page is classified by the window it is
// handed, through the same first-frame twin the whole window uses, so no new
// decision is defined here.
//
// THE WEB'S ONE SPREAD-VOCABULARY MODULE. `canSplit` asks the facade a second
// time with the `two_pages` preference, and the "not yet answered" layout is
// `one_page`: the two places the web must write a spread word as a value. So
// this file takes the single 6.9.26p AC6 exemption (7.10.26v §7), and every
// other module imports `PAGED` from here rather than quoting a word.

import type { Spread, SpreadInput, SpreadLayout, SpreadPreference } from '../../wasm'
import type { WindowClass } from '../useWindowClass'

/** The two pages as the shell lays them out, plus the rule's raw answer. */
export interface SpreadValue {
  /** From the facade; `one_page` before it resolves. */
  layout: SpreadLayout
  /** The leading page's own class (the window's unless `TwoPages`). */
  leading: WindowClass
  /** The companion's (the window's unless `TwoPages`). */
  companion: WindowClass
  /** As u returned them — at `Tabletop` the top half's height (z reads these). */
  splitDp: number
  gapDp: number
  /** CSS px for the grid template; 0 unless paged. */
  companionPx: number
  gapPx: number
  /** Would `TwoPages` be honoured here? The settings section's relevance (§8). */
  canSplit: boolean
  preference: SpreadPreference
  /** Has the facade answered? Until then `layout` is a placeholder, never reported (§6). */
  answered: boolean
}

/** Which layouts draw two side-by-side pages. The web's one copy (w imports it). */
export const PAGED: Record<SpreadLayout, boolean> = { one_page: false, two_pages: true, tabletop: false }

/** The facade's `spreadLayout` (u §5). */
export type SpreadFn = (input: SpreadInput) => Spread
/** `windowSizeTable.ts`'s twin, as `useWindowClass` composes it. */
export type Classify = (widthDp: number, heightDp: number) => WindowClass

export interface SpreadRequest {
  /** `null` until the facade resolves, or after a failed init. */
  ask: SpreadFn | null
  /** Width, height and §1's fold signals, in dp. */
  measured: Omit<SpreadInput, 'preference'>
  preference: SpreadPreference
  rtl: boolean
  /** Root scale (CSS px per dp), for the px fields. */
  scale: number
}

const FORCED_SPLIT: SpreadPreference = 'two_pages'
const UNANSWERED: Spread = { layout: 'one_page', splitDp: 0, gapDp: 0 }

/** The rule's answer, or `one_page` when there is no facade or it throws. */
function answer(ask: SpreadFn | null, input: SpreadInput): Spread {
  if (ask === null) return UNANSWERED
  try {
    return ask(input)
  } catch {
    return UNANSWERED
  }
}

/** The spread for one measurement; `classify` is the whole window's classifier. */
export function readSpread(request: SpreadRequest, classify: Classify): SpreadValue {
  const { ask, measured, preference, rtl, scale } = request
  const whole = classify(measured.widthDp, measured.heightDp)
  const spread = answer(ask, { ...measured, preference })
  const forced = preference === FORCED_SPLIT ? spread : answer(ask, { ...measured, preference: FORCED_SPLIT })
  const base = { layout: spread.layout, splitDp: spread.splitDp, gapDp: spread.gapDp, canSplit: PAGED[forced.layout], preference, answered: ask !== null }
  if (!PAGED[spread.layout]) return { ...base, leading: whole, companion: whole, companionPx: 0, gapPx: 0 }

  const left = spread.splitDp
  const right = measured.widthDp - spread.splitDp - spread.gapDp
  const [leadingDp, companionDp] = rtl ? [right, left] : [left, right]
  return {
    ...base,
    leading: classify(leadingDp, measured.heightDp),
    companion: classify(companionDp, measured.heightDp),
    companionPx: companionDp * scale,
    gapPx: spread.gapDp * scale,
  }
}
